"use client"

import { CommentResp } from "@types"
import Comment from "./Comment"
import { usePromiseRouter } from "@/app/hooks/usePromiseRouter"
import { useState } from "react"

/**
 * A tab that displays all comments.
 *
 * Right now we support "All", "Farcaster", and "Paragraph" tabs.
 *
 * The tab component itself is agnostic as to its data source.
 */
export default function CommentsTab({
  comments,
  postId,
}: {
  comments: CommentResp[]
  postId: string
}) {
  const router = usePromiseRouter()

  const [isReplyingToCommentId, setIsReplyingToCommentId] = useState<
    string | null
  >(null)
  const [replyInProgress, setReplyInProgress] = useState(false)

  const [isEditingCommentId, setIsEditingCommentId] = useState<string | null>(
    null
  )
  const [editInProgress, setEditInProgress] = useState(false)

  const handleSetIsReplyingToCommentId = (commentId: string) => {
    if (isReplyingToCommentId === commentId) {
      setIsReplyingToCommentId(null)
    } else {
      setIsReplyingToCommentId(commentId)
    }
  }

  const handleReplyStart = () => {
    setReplyInProgress(true)
  }

  const handleReplyEnd = () => {
    setReplyInProgress(false)
  }

  const handleSetIsEditingCommentId = (commentId: string) => {
    // If the comment is already being edited, we want to stop editing it when the user clicks the edit button again.
    if (isEditingCommentId === commentId) {
      setIsEditingCommentId(null)
    } else {
      // If the comment is not being edited, we want to start editing it.
      setIsEditingCommentId(commentId)
    }
  }

  const handleEditStart = () => {
    setEditInProgress(true)
  }

  const handleEditEnd = () => {
    setEditInProgress(false)
  }

  return (
    <div>
      {comments.map((comment) => (
        <Comment
          key={comment.id}
          depth={0}
          comment={comment}
          postId={postId}
          refresh={router.refresh}
          isEditingCommentId={isEditingCommentId}
          handleSetIsEditingCommentId={handleSetIsEditingCommentId}
          isEditInProgress={editInProgress}
          handleEditStart={handleEditStart}
          handleEditEnd={handleEditEnd}
          isReplyingToCommentId={isReplyingToCommentId}
          handleSetIsReplyingToCommentId={handleSetIsReplyingToCommentId}
          isReplyInProgress={replyInProgress}
          handleReplyStart={handleReplyStart}
          handleReplyEnd={handleReplyEnd}
        />
      ))}
    </div>
  )
}
